import { useEffect, useState, type ComponentType } from "react";
import { api } from "../api";
import { Avatar } from "../avatar";
import { BookOpenIcon, BrainIcon, ChatIcon, ClockIcon, ListChecksIcon, LogOutIcon, LoopIcon, MoonIcon, NotebookIcon, SunIcon, WalletIcon } from "../icons";
import { listGlance } from "./loopGlance";

export type Page = "chat" | "loops" | "memory" | "notes" | "project" | "research" | "schedules" | "tasks" | "usage";
export type NavPage = Exclude<Page, "chat" | "project">;

/** What waits on the person, per page. */
export type NavBadges = Partial<Record<NavPage, number>>;

export const NAV_PAGES: Array<{ page: NavPage; label: string; Icon: ComponentType<{ size?: number }>; tint: string }> = [
	{ page: "loops", label: "Loops", Icon: LoopIcon, tint: "green" },
	{ page: "memory", label: "记忆", Icon: BrainIcon, tint: "violet" },
	{ page: "notes", label: "笔记", Icon: NotebookIcon, tint: "yellow" },
	{ page: "research", label: "研究", Icon: BookOpenIcon, tint: "teal" },
	{ page: "schedules", label: "定时", Icon: ClockIcon, tint: "blue" },
	{ page: "tasks", label: "任务", Icon: ListChecksIcon, tint: "rose" },
	{ page: "usage", label: "费用", Icon: WalletIcon, tint: "lime" },
];

export function badgeTitle(page: NavPage, count: number): string {
	if (page === "loops") return `${count} 个待处理`;
	if (page === "schedules") return `${count} 个失败`;
	return `${count} 个变更待部署`;
}

const POLL_MS = 60_000;

/** Loops waiting on the person, schedules whose last firing failed, and changes waiting to deploy. Refreshed when the page changes. */
export function useNavBadges(enabled: boolean, page: Page): NavBadges {
	const [badges, setBadges] = useState<NavBadges>({});
	useEffect(() => {
		if (!enabled) return;
		let live = true;
		const load = () => {
			const now = Date.now();
			const count = (key: NavPage, read: Promise<number>) =>
				read.then(
					(n) => live && setBadges((b) => ({ ...b, [key]: n })),
					() => {},
				);
			void count("loops", api.loops().then((r) => r.loops.filter((l) => listGlance(l, now).group === "act").length));
			void count("schedules", api.schedules().then((r) => r.schedules.filter((s) => s.lastFiring?.status === "failed").length));
			void count("tasks", api.deploymentBatch().then((r) => r.ready.length));
		};
		load();
		const timer = setInterval(load, POLL_MS);
		return () => {
			live = false;
			clearInterval(timer);
		};
	}, [enabled, page]);
	return badges;
}

export function Badge({ page, count }: { page: NavPage; count?: number }) {
	if (!count) return null;
	return (
		<span className="badge" title={badgeTitle(page, count)}>
			{count}
		</span>
	);
}

/** The wide screen's page rail: chat and every page, then theme, logout and the person. */
export function Rail(props: {
	page: Page;
	badges: NavBadges;
	userName: string;
	theme: "dark" | "light";
	onOpenChat: () => void;
	onOpenPage: (page: NavPage) => void;
	onThemeToggle: () => void;
	onLogout: () => void;
}) {
	const themeLabel = props.theme === "dark" ? "切换到日间配色" : "切换到夜间配色";
	const onChat = props.page === "chat" || props.page === "project";
	return (
		<nav className="rail" aria-label="页面">
			<button className={`rail-item${onChat ? " current" : ""}`} aria-current={onChat ? "page" : undefined} aria-label="对话" title="对话" onClick={props.onOpenChat}>
				<ChatIcon size={22} />
			</button>
			{NAV_PAGES.map(({ page, label, Icon }) => (
				<button
					key={page}
					className={`rail-item${props.page === page ? " current" : ""}`}
					aria-current={props.page === page ? "page" : undefined}
					aria-label={label}
					title={label}
					onClick={() => props.onOpenPage(page)}
				>
					<Icon size={22} />
					<Badge page={page} count={props.badges[page]} />
				</button>
			))}
			<span className="rail-gap" />
			<button className="rail-item" aria-label={themeLabel} title={themeLabel} onClick={props.onThemeToggle}>
				{props.theme === "dark" ? <SunIcon size={21} /> : <MoonIcon size={21} />}
			</button>
			<button className="rail-item" aria-label="退出" title="退出" onClick={props.onLogout}>
				<LogOutIcon size={21} />
			</button>
			<span title={props.userName}>
				<Avatar name={props.userName} size={36} />
			</span>
		</nav>
	);
}
